// Minimal, conversion-optimized player: native <video> + hls.js, small control
// cluster in a corner, muted autoplay with a tap-for-sound cue.
const { attachSource } = require('./mediaSource');

const ICONS = {
  play: 'controls-play',
  pause: 'controls-pause',
  replay: 'controls-repeat',
  volumeOn: 'controls-volumeon',
  volumeOff: 'controls-volumeoff',
  fullscreenEnter: 'editor-expand',
  fullscreenExit: 'editor-contract',
};

const DEFAULT_ELEMENTS = ['play_pause', 'mute', 'volume'];
const DEFAULT_OPTIONS = ['loop', 'unmute_prompt', 'restart_on_unmute', 'pause_offscreen'];

const VISIBLE_RATIO = 0.5; // autoplay / resume once half of the video is visible
const HIDDEN_RATIO = 0.2;  // pause when less than this is visible

function label(key, fallback) {
  const i18n = (window.$madSettings && window.$madSettings.i18n) || {};
  return i18n[key] || fallback;
}

function prefersReducedMotion() {
  return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function saveDataEnabled() {
  return !!(navigator.connection && navigator.connection.saveData);
}

function createButton(className, ariaLabel, icon) {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = `mad-minimal__button ${className}`;
  button.setAttribute('aria-label', ariaLabel);
  button.innerHTML = `<span class="dashicons dashicons-${icon}" aria-hidden="true"></span>`;
  return button;
}

function setIcon(button, icon) {
  const span = button && button.querySelector('.dashicons');
  if (span) span.className = `dashicons dashicons-${icon}`;
}

class MinimalPlayer {
  /**
   * @param {HTMLElement} mount   The .mad-video element inside the container (will be emptied).
   * @param {Object}  options
   * @param {string}  options.url
   * @param {string}  [options.poster]
   * @param {boolean} [options.autoplay=false]       Muted autoplay once scrolled into view.
   * @param {boolean} [options.startWithSound=false] Start right away with sound (call from a click handler).
   * @param {number}  [options.volume=0.5]           Volume when sound is on (0–1).
   * @param {Object}  [options.config]               {elements: [], position: '', options: []} from PHP.
   */
  constructor(mount, options) {
    this.mount = mount;
    this.container = mount.closest('.mad-video__container') || mount;
    this.url = options.url;
    this.poster = options.poster || null;
    this.volume = typeof options.volume === 'number' ? Math.min(1, Math.max(0, options.volume)) : 0.5;

    const config = options.config || {};
    this.elements = config.elements || DEFAULT_ELEMENTS;
    const opts = config.options || DEFAULT_OPTIONS;
    this.loop = opts.includes('loop');
    this.showUnmuteCue = opts.includes('unmute_prompt');
    this.restartOnUnmute = opts.includes('restart_on_unmute');
    this.pauseOffscreen = opts.includes('pause_offscreen');

    this.autoplay = !!options.autoplay && !prefersReducedMotion() && !saveDataEnabled();
    this.startWithSound = !!options.startWithSound;

    this.mutedByAutoplay = false;   // playing muted because of autoplay → first unmute may restart
    this.pausedOffscreen = false;   // paused by scrolling away → resume when visible again
    this.started = false;

    this.build();
    this.bindEvents();
    this.attach();

    if (this.startWithSound) {
      this.playWithSound();
    }
    if (this.autoplay || this.pauseOffscreen) {
      this.observeVisibility();
    }
    this.updateUi();
  }

  /* ─────────────── DOM ─────────────── */

  build() {
    this.mount.innerHTML = '';

    const root = document.createElement('div');
    root.className = 'mad-minimal';

    const video = document.createElement('video');
    video.className = 'mad-minimal__video';
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.loop = this.loop;
    video.tabIndex = 0;
    video.setAttribute('aria-label', label('video', 'Video'));
    if (this.poster) video.poster = this.poster;
    // Nothing is downloaded until needed, unless the video starts right away
    video.preload = this.autoplay || this.startWithSound ? 'metadata' : 'none';
    root.appendChild(video);

    const spinner = document.createElement('div');
    spinner.className = 'mad-minimal__spinner';
    spinner.setAttribute('aria-hidden', 'true');
    root.appendChild(spinner);

    const cue = createButton('mad-minimal__cue', label('unmute', 'Turn sound on'), ICONS.volumeOff);
    cue.hidden = true;
    root.appendChild(cue);

    const controls = document.createElement('div');
    controls.className = 'mad-minimal__controls';

    if (this.elements.includes('play_pause')) {
      this.playButton = createButton('mad-minimal__play', label('play', 'Play'), ICONS.play);
      controls.appendChild(this.playButton);
    }

    if (this.elements.includes('mute')) {
      const sound = document.createElement('div');
      sound.className = 'mad-minimal__sound';

      this.muteButton = createButton('mad-minimal__mute', label('mute', 'Mute'), ICONS.volumeOn);
      sound.appendChild(this.muteButton);

      if (this.elements.includes('volume')) {
        const slider = document.createElement('input');
        slider.type = 'range';
        slider.min = '0';
        slider.max = '1';
        slider.step = '0.05';
        slider.className = 'mad-minimal__volume';
        slider.setAttribute('aria-label', label('volume', 'Volume'));
        this.volumeSlider = slider;
        sound.appendChild(slider);
      }

      controls.appendChild(sound);
    }

    if (this.elements.includes('fullscreen') && this.fullscreenSupported(video)) {
      this.fullscreenButton = createButton('mad-minimal__fullscreen', label('fullscreen', 'Fullscreen'), ICONS.fullscreenEnter);
      controls.appendChild(this.fullscreenButton);
    }

    if (controls.children.length) {
      root.appendChild(controls);
    }

    this.mount.appendChild(root);
    this.root = root;
    this.video = video;
    this.cue = cue;
  }

  attach() {
    const lazy = !this.autoplay && !this.startWithSound;
    try {
      this.source = attachSource(this.video, this.url, { lazy });
    } catch (error) {
      console.error('MAD Videos:', error.message);
      this.source = { hls: null, startLoad() {} };
    }
  }

  /* ─────────────── Events ─────────────── */

  bindEvents() {
    const video = this.video;

    video.addEventListener('click', () => this.onSurfaceClick());
    this.cue.addEventListener('click', () => this.unmuteFromAutoplay());

    if (this.playButton) {
      this.playButton.addEventListener('click', () => this.togglePlay());
    }
    if (this.muteButton) {
      this.muteButton.addEventListener('click', () => this.toggleMute());
    }
    if (this.volumeSlider) {
      this.volumeSlider.addEventListener('input', () => this.setVolume(parseFloat(this.volumeSlider.value)));
    }
    if (this.fullscreenButton) {
      this.fullscreenButton.addEventListener('click', () => this.toggleFullscreen());
      document.addEventListener('fullscreenchange', () => this.updateUi());
      document.addEventListener('webkitfullscreenchange', () => this.updateUi());
    }

    // Keyboard on the focused video: space / k = play/pause, m = mute
    video.addEventListener('keydown', (e) => {
      if (e.key === ' ' || e.key === 'k') {
        e.preventDefault();
        this.onSurfaceClick();
      } else if (e.key === 'm') {
        this.toggleMute();
      }
    });

    video.addEventListener('play', () => {
      this.source.startLoad(); // lazy HLS: fetch segments only now
      this.started = true;
      this.container.classList.add('active');
    });
    video.addEventListener('waiting', () => this.root.classList.add('is-buffering'));
    ['playing', 'pause', 'canplay', 'ended'].forEach((type) => {
      video.addEventListener(type, () => this.root.classList.remove('is-buffering'));
    });
    ['play', 'pause', 'ended', 'volumechange'].forEach((type) => {
      video.addEventListener(type, () => this.updateUi());
    });
  }

  observeVisibility() {
    if (!('IntersectionObserver' in window)) {
      if (this.autoplay) this.startMutedAutoplay();
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const ratio = entry.isIntersecting ? entry.intersectionRatio : 0;

        if (ratio >= VISIBLE_RATIO) {
          if (!this.started && this.autoplay) {
            this.startMutedAutoplay();
          } else if (this.pausedOffscreen) {
            this.pausedOffscreen = false;
            this.play();
          }
        } else if (ratio < HIDDEN_RATIO && this.pauseOffscreen && !this.video.paused) {
          this.pausedOffscreen = true;
          this.video.pause();
        }
      });
    }, { threshold: [0, HIDDEN_RATIO, VISIBLE_RATIO] });

    observer.observe(this.container);
  }

  /* ─────────────── Playback ─────────────── */

  play() {
    const promise = this.video.play();
    return promise && promise.catch ? promise : Promise.resolve();
  }

  startMutedAutoplay() {
    this.video.muted = true;
    this.mutedByAutoplay = true;
    this.play().catch(() => {
      // Autoplay blocked (e.g. iOS low power mode): fall back to the play button
      this.mutedByAutoplay = false;
      this.updateUi();
    });
    this.updateUi();
  }

  playWithSound() {
    this.video.muted = false;
    this.video.volume = this.volume;
    this.play().catch(() => {
      // Sound not allowed without a direct tap: play muted and show the cue instead
      this.startMutedAutoplay();
    });
  }

  unmuteFromAutoplay() {
    this.mutedByAutoplay = false;
    if (this.restartOnUnmute) {
      this.video.currentTime = 0;
    }
    this.video.muted = false;
    this.video.volume = this.volume || 0.5; // the video's "Default volume" (desktop; iOS ignores it)
    this.play().catch(() => {});
    this.updateUi();
  }

  onSurfaceClick() {
    if (this.mutedByAutoplay) {
      this.unmuteFromAutoplay();
    } else {
      this.togglePlay();
    }
  }

  togglePlay() {
    this.pausedOffscreen = false;
    if (this.video.paused || this.video.ended) {
      if (!this.started && !this.mutedByAutoplay) {
        // First start by the viewer: with sound
        this.playWithSound();
      } else {
        this.play().catch(() => {});
      }
    } else {
      this.video.pause();
    }
  }

  toggleMute() {
    if (this.mutedByAutoplay) {
      this.unmuteFromAutoplay();
      return;
    }
    if (this.video.muted || this.video.volume === 0) {
      this.video.muted = false;
      this.video.volume = this.volume || 0.5;
    } else {
      this.video.muted = true;
    }
  }

  setVolume(value) {
    if (this.mutedByAutoplay && value > 0) {
      this.unmuteFromAutoplay();
    }
    this.video.volume = value;
    this.video.muted = value === 0;
    if (value > 0) this.volume = value;
  }

  /* ─────────────── Fullscreen ─────────────── */

  fullscreenSupported(video) {
    const el = this.container;
    return !!(el.requestFullscreen || el.webkitRequestFullscreen || video.webkitEnterFullscreen);
  }

  isFullscreen() {
    return !!(document.fullscreenElement || document.webkitFullscreenElement);
  }

  toggleFullscreen() {
    if (this.isFullscreen()) {
      (document.exitFullscreen || document.webkitExitFullscreen).call(document);
      return;
    }

    const el = this.container;
    if (el.requestFullscreen) {
      el.requestFullscreen();
    } else if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
    } else if (this.video.webkitEnterFullscreen) {
      this.video.webkitEnterFullscreen(); // iPhone: native fullscreen player
    }
  }

  /* ─────────────── UI state ─────────────── */

  updateUi() {
    const video = this.video;
    const playing = !video.paused && !video.ended;
    const muted = video.muted || video.volume === 0;

    this.root.classList.toggle('is-playing', playing);
    this.root.classList.toggle('is-muted', muted);

    if (this.playButton) {
      const ended = video.ended && !this.loop;
      setIcon(this.playButton, playing ? ICONS.pause : (ended ? ICONS.replay : ICONS.play));
      this.playButton.setAttribute('aria-label', playing ? label('pause', 'Pause') : (ended ? label('replay', 'Replay') : label('play', 'Play')));
    }

    if (this.muteButton) {
      setIcon(this.muteButton, muted ? ICONS.volumeOff : ICONS.volumeOn);
      this.muteButton.setAttribute('aria-label', muted ? label('unmute', 'Turn sound on') : label('mute', 'Mute'));
      this.muteButton.setAttribute('aria-pressed', muted ? 'true' : 'false');
    }

    if (this.volumeSlider) {
      const level = muted ? 0 : video.volume;
      this.volumeSlider.value = String(level);
      // filled part of the custom-styled track (WebKit/Blink; Firefox uses ::-moz-range-progress)
      this.volumeSlider.style.setProperty('--mad-volume-fill', `${Math.round(level * 100)}%`);
    }

    if (this.fullscreenButton) {
      const fullscreen = this.isFullscreen();
      setIcon(this.fullscreenButton, fullscreen ? ICONS.fullscreenExit : ICONS.fullscreenEnter);
      this.fullscreenButton.setAttribute('aria-label', fullscreen ? label('exitFullscreen', 'Exit fullscreen') : label('fullscreen', 'Fullscreen'));
    }

    this.cue.hidden = !(this.mutedByAutoplay && this.showUnmuteCue);
  }
}

module.exports = MinimalPlayer;
